import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ActionListItem, Box, Button, Icon } from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { useDevXContext } from 'hooks/useDevXContext'
import { useDevXGetStartedStepTracking } from 'hooks/useDevXGetStartedStepTracking'
import { StepKeys } from 'pages/Dashboard/constants'
import { StepKey } from 'pages/Dashboard/types'
import { AmplitudeEvent, AmplitudeEventType } from 'utils/amplitude/amplitudeEvents'
import { isSandbox, isTest } from 'utils/environment'
import { Amp } from 'utils/tracker'

export interface GetStartedStepsProps {
  isLoading: boolean
  canViewApiKeys: boolean
  hasApiKeys: boolean
  hasMerchants: boolean
  hasPayments: boolean
}

export const GetStartedSteps = ({
  isLoading,
  canViewApiKeys,
  hasApiKeys,
  hasMerchants,
  hasPayments,
}: GetStartedStepsProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isDevXGoLiveReady } = useDevXContext()

  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  const { applyStepTracking, isStepCompleted, getStepState, onCompleteStep, onCompleteSteps } =
    useDevXGetStartedStepTracking(canViewApiKeys)

  const trackDevXEvent = (eventLabel: AmplitudeEventType) => {
    if (isSandbox || isTest) Amp.track(eventLabel)
  }

  const isLoadingStepStatus = useMemo(
    () => (applyStepTracking ? isLoading : false),
    [applyStepTracking, isLoading]
  )

  const toggleExpanded = (itemName: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(itemName)) {
        newSet.delete(itemName)
      } else {
        newSet.add(itemName)
      }
      return newSet
    })
  }

  const createApiKeyItem = {
    name: StepKeys.CREATE_API_KEY,
    title: t('getStarted.generateYourAPIKeys'),
    iconName: 'Code' as const,
    description: t('getStarted.generateYourAPIKeysDescription'),
    actions: [
      {
        label: t('manageApiKeys.generateAPIKeyBtn'),
        testid: 'generate-api-key-btn',
        onClick: () => {
          trackDevXEvent(AmplitudeEvent.DEVX_SANDBOX_CLICKED_GENERATE_API_KEY)
          navigate(`${PathNames.INTEGRATIONS}`)
        },
      },
    ],
  }

  const stepItems = [
    {
      name: StepKeys.USE_API_KEY,
      title: t('getStarted.bvnkApis'),
      iconName: 'Question' as const,
      description: t('getStarted.bvnkApisDescription'),
      actions: [
        {
          label: t('getStarted.readDocs'),
          testid: 'read-api-docs-btn',
          onClick: () => {
            trackDevXEvent(AmplitudeEvent.DEVX_SANDBOX_CLICKED_READ_DOCS)
            onCompleteStep(StepKeys.USE_API_KEY)
            window.open(`${process.env.DEVELOPER_DOCS}/getting-started#generate-api-keys`)
          },
        },
        {
          label: t('getStarted.readApiRef'),
          testid: 'read-api-ref-docs-btn',
          onClick: () => {
            trackDevXEvent(AmplitudeEvent.DEVX_SANDBOX_CLICKED_READ_API_REF)
            onCompleteStep(StepKeys.USE_API_KEY)
            window.open(`${process.env.API_REFERENCE}`)
          },
        },
      ],
    },
    {
      name: StepKeys.VIEW_WALLETS,
      title: t('getStarted.viewYourWallets'),
      iconName: 'Wallet' as const,
      description: t('getStarted.viewYourWalletsDescription'),
      actions: [
        {
          label: t('getStarted.viewWallets'),
          testid: 'view-wallets-btn',
          onClick: async () => {
            trackDevXEvent(AmplitudeEvent.DEVX_SANDBOX_CLICKED_VIEW_WALLETS)
            await onCompleteStep(StepKeys.VIEW_WALLETS)
            navigate(`${PathNames.WALLETS}${PathNames.WALLET_DASHBOARD}`)
          },
        },
        {
          label: t('learnMore'),
          testid: 'wallets-learn-more-btn',
          onClick: () => {
            trackDevXEvent(AmplitudeEvent.DEVX_SANDBOX_CLICKED_LEARN_ABOUT_WALLETS)
            window.open(`${process.env.DEVELOPER_DOCS}/currencies-1`)
          },
        },
      ],
    },
    {
      name: StepKeys.CREATE_MERCHANT,
      title: t('createAMerchant'),
      iconName: 'Storefront' as const,
      description: t('getStarted.createMerchantDescription'),
      actions: [
        {
          label: t('createAMerchant'),
          testid: 'create-merchant-btn',
          onClick: () => {
            trackDevXEvent(AmplitudeEvent.DEVX_SANDBOX_CLICKED_CREATE_MERCHANT)
            navigate(`${PathNames.SETTINGS}${PathNames.MANAGE_MERCHANT_DETAILS}`)
          },
        },
        {
          label: t('learnMore'),
          testid: 'merchant-learn-more-btn',
          onClick: () => {
            trackDevXEvent(AmplitudeEvent.DEVX_SANDBOX_CLICKED_MERCHANT_LEARN_MORE)
            window.open(`${process.env.DEVELOPER_DOCS}/creating-your-first-merchant`)
          },
        },
      ],
    },
    {
      name: StepKeys.CREATE_PAYMENT,
      title: t('createAPayment'),
      iconName: 'CreditCard' as const,
      description: t('getStarted.createPaymentDescription'),
      actions: [
        {
          label: t('createAPayment'),
          testid: 'create-payment-btn',
          onClick: () => {
            trackDevXEvent(AmplitudeEvent.DEVX_SANDBOX_CLICKED_CREATE_PAYMENT)
            navigate(`${PathNames.PAYMENTS}${PathNames.CRYPTO}`)
          },
        },
      ],
    },
    {
      name: StepKeys.GO_LIVE,
      title: t('getStarted.howToGoLive'),
      iconName: 'ListChecks' as const,
      description: t('getStarted.goLiveDescription'),
      actions: [
        {
          label: t('contactSales'),
          testid: 'contact-sales-btn',
          onClick: () => {
            trackDevXEvent(AmplitudeEvent.DEVX_SANDBOX_CLICKED_CONTACT_SALES)
            onCompleteStep(StepKeys.GO_LIVE)
            window.open(`${process.env.CONTACT_SALES_URL}`)
          },
        },
      ],
    },
  ]

  const allStepItems = canViewApiKeys ? [createApiKeyItem, ...stepItems] : stepItems

  useEffect(() => {
    if (isDevXGoLiveReady && applyStepTracking && !isStepCompleted(StepKeys.GO_LIVE)) {
      onCompleteStep(StepKeys.GO_LIVE)
    }
  }, [isDevXGoLiveReady, applyStepTracking])

  useEffect(() => {
    if (!isLoading && applyStepTracking) {
      const dataSteps: Partial<Record<StepKey, boolean>> = {
        createApiKey: hasApiKeys,
        createMerchant: hasMerchants,
        createPayment: hasPayments,
      }
      const completedDataSteps: StepKey[] = Object.entries(dataSteps)
        ?.filter((step: [StepKey, boolean]) => step?.[1])
        ?.map((stepEntry: [StepKey, boolean]) => stepEntry?.[0])

      if (completedDataSteps?.length) {
        onCompleteSteps(completedDataSteps)
      }
    }
  }, [isLoading, applyStepTracking])

  return (
    <Box paddingT={16} paddingB={8} flexCol>
      {allStepItems?.map(item => {
        const isExpanded = expandedItems.has(item?.name)

        return (
          <Box key={item?.name} flexCol className="border border-stroke-static ">
            <ActionListItem
              id={item?.name}
              testId={`get-started-step-${item?.name}`}
              title={item?.title}
              Avatar={
                <Box flex centerChildren size={40} background="subtle" flexSize="keep">
                  {!isLoadingStepStatus ? (
                    <Icon name={item?.iconName} size="large" />
                  ) : (
                    <Box className="animate-spin">
                      <Icon name="Spinner" />
                    </Box>
                  )}
                </Box>
              }
              CustomAction={
                <Button
                  variant="ghost"
                  iconElement={<Icon name={isExpanded ? 'CaretDown' : 'CaretRight'} size="small" />}
                  onClick={() => toggleExpanded(item?.name)}
                  testId={`expand-${item?.name}`}
                />
              }
              {...(applyStepTracking &&
                !isLoadingStepStatus && {
                  iconState: getStepState(item?.name as StepKey),
                })}
              border={false}
            />

            {isExpanded && (
              <Box paddingX={16}>
                <p className="text-body-regular-small text-foreground-subdued">
                  {item?.description}
                </p>
                <Box flex gapX={8} className="py-2">
                  {item?.actions?.map((action, actionIndex) => (
                    <Button
                      key={`${item?.name}-action-${actionIndex}`}
                      testId={action?.testid}
                      variant={actionIndex === 0 ? 'primary' : 'secondary'}
                      onClick={action?.onClick}
                    >
                      {action?.label}
                    </Button>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )
      })}
    </Box>
  )
}
